import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { navigate } from '@reach/router';
import Validator from 'validator';

import { useCrmList, useCreateNegotiatorAccount } from '@api';
import { routes } from '@particles';

/**
 * Create a Negotiator company account.
 *
 * Until now a company could only be created through the extension's public
 * sign-up, which sets no CRM. That is fine for Reapit — a negotiator signs in
 * against the CRM and their `crmId` is filled in on the way through — but it
 * makes a company-scoped CRM unreachable: its token cannot be entered before
 * the CRM is known, and its negotiators cannot sign in before the token exists.
 *
 * Choosing the CRM at creation is what breaks that cycle, which is why it is a
 * required field here rather than something to set later.
 */

export const CreateNegotiatorAccount: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [crmId, setCrmId] = useState('');
  const [website, setWebsite] = useState('');
  const [address, setAddress] = useState('');

  const { data: crms } = useCrmList();
  const createAccount = useCreateNegotiatorAccount();

  const selected = (crms ?? []).find((c) => c.id === crmId);

  const close = () => {
    setOpen(false);
    setName('');
    setCrmId('');
    setWebsite('');
    setAddress('');
    createAccount.reset();
  };

  // Matches the server's @IsUrl on Companies.website, which is validator.js's
  // isURL — the same rule, so a value accepted here is not rejected on save.
  const websiteInvalid = website.trim().length > 0 && !Validator.isURL(website.trim());

  const submit = () =>
    createAccount.mutate(
      {
        name: name.trim(),
        crmId,
        website: website.trim(),
        address: address.trim()
      },
      {
        // Straight to the new account: the next steps (token, branches,
        // licence) all live on its detail page, so stopping at a toast would
        // leave the admin to go and find it.
        onSuccess: (created) => {
          close();
          if (created?.id) navigate(`${routes.SA_NegotiatorDetails}/${created.id}`);
        }
      }
    );

  return (
    <>
      <Button variant="contained" onClick={() => setOpen(true)}>
        Create Account
      </Button>

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Create Negotiator account</DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Company name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText="Must be unique."
            />

            <TextField
              select
              fullWidth
              size="small"
              label="CRM"
              value={crmId}
              onChange={(e) => setCrmId(e.target.value)}
              helperText="Determines how negotiators sign in and how the CRM is connected."
            >
              {(crms ?? []).map((crm) => (
                <MenuItem key={crm.id} value={crm.id}>
                  {crm.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              size="small"
              label="Website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              error={websiteInvalid}
              helperText={websiteInvalid ? 'That does not look like a web address.' : 'Optional.'}
            />

            <TextField
              fullWidth
              size="small"
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              multiline
              minRows={2}
              helperText="Optional."
            />

            {/* Spell out the consequence of the choice — the two CRM shapes
                onboard completely differently, and picking the wrong one is
                only obvious much later. */}
            {selected && (
              <Alert severity="info">
                {selected.requiresCompanyToken ? (
                  <>
                    <strong>{selected.name}</strong> uses one company-wide token. You&apos;ll add it on
                    the next screen, then sync branches. Negotiators sign in with a link sent to their
                    work email.
                  </>
                ) : (
                  <>
                    <strong>{selected.name}</strong> authenticates each negotiator individually — they
                    sign in through {selected.name} itself. There is no company token to set.
                  </>
                )}
              </Alert>
            )}

            {createAccount.isError && (
              <Alert severity="error">
                {(createAccount.error as Error)?.message ?? 'Could not create the account.'}
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary">
              Licences and users are added on the account page once it exists.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!name.trim() || !crmId || websiteInvalid || createAccount.isPending}
            onClick={submit}
          >
            {createAccount.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateNegotiatorAccount;
